import { useCallback, useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ApiError } from '@/lib/api/client';
import { accessRequestApi } from '@/lib/api/endpoints';
import type {
  AccessRequestForm,
  AccessRequestPayload,
  AccessRequestRecord,
} from '@/lib/api/types';
import { routes } from '@/lib/routes';
import { useI18n } from '@/hooks/useI18n';
import { useToast } from '@/hooks/useToast';
import { AuthCard } from './components/AuthCard';

const STORAGE_KEY = 'za_access_request_id';

type Step = 'category' | 'form' | 'verify' | 'ready' | 'submitted';

const RequestAccessPage = () => {
  const { t, locale } = useI18n();
  const { notify } = useToast();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const [catalog, setCatalog] = useState<AccessRequestForm | null>(null);
  const [record, setRecord] = useState<AccessRequestRecord | null>(null);
  const [step, setStep] = useState<Step>('category');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [categoryCode, setCategoryCode] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [admissionYear, setAdmissionYear] = useState<number | ''>('');
  const [programCode, setProgramCode] = useState('');
  const [admissionPeriod, setAdmissionPeriod] = useState('');
  const [educationLevel, setEducationLevel] = useState('');
  const [specialtyCode, setSpecialtyCode] = useState('');
  const [comment, setComment] = useState('');
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setInterval(() => {
      setCooldown((c) => (c <= 1 ? 0 : c - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [cooldown]);

  const syncFromRecord = useCallback((next: AccessRequestRecord) => {
    setRecord(next);
    localStorage.setItem(STORAGE_KEY, next.id);
    setCategoryCode(next.category_code);
    setFirstName(next.first_name);
    setLastName(next.last_name);
    setEmail(next.email);
    setAdmissionYear(next.admission_year ?? '');
    setProgramCode(next.program_code ?? '');
    setAdmissionPeriod(next.admission_period ?? '');
    setEducationLevel(next.education_level ?? '');
    setSpecialtyCode(next.specialty_code ?? '');
    setComment(next.comment ?? '');
    if (next.can_resend_in && next.can_resend_in > 0) {
      setCooldown(next.can_resend_in);
    }

    if (next.status === 'PENDING_OWNER_REVIEW' || next.status === 'APPROVED' || next.status === 'REJECTED') {
      setStep('submitted');
    } else if (next.email_status === 'VERIFIED' && next.can_submit) {
      setStep('ready');
    } else if (next.status === 'DRAFT') {
      setStep(next.email_status === 'VERIFIED' ? 'ready' : 'verify');
    } else {
      setStep('category');
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const form = await accessRequestApi.form();
        if (cancelled) return;
        setCatalog(form);

        const resumeId = params.get('request')?.trim() || localStorage.getItem(STORAGE_KEY);
        if (resumeId) {
          const existing = await accessRequestApi.read(resumeId);
          if (!cancelled) syncFromRecord(existing);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : t('common.error'));
        }
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- initial load only
  }, [params, syncFromRecord, t]);

  const specialties =
    catalog && educationLevel ? (catalog.supervised.specialties[educationLevel] ?? []) : [];

  const buildPayload = (): AccessRequestPayload => ({
    category_code: categoryCode,
    first_name: firstName.trim(),
    last_name: lastName.trim(),
    email: email.trim().toLowerCase(),
    admission_year: categoryCode === 'DOCTORAL_STUDENT' && admissionYear !== '' ? Number(admissionYear) : null,
    program_code: categoryCode === 'DOCTORAL_STUDENT' ? programCode || null : null,
    admission_period: categoryCode === 'DOCTORAL_STUDENT' ? admissionPeriod || null : null,
    education_level: categoryCode === 'SUPERVISED_STUDENT' ? educationLevel || null : null,
    specialty_code: categoryCode === 'SUPERVISED_STUDENT' ? specialtyCode || null : null,
    locale,
    comment: comment.trim() || null,
  });

  const handleSelectCategory = (code: string) => {
    setCategoryCode(code);
    setError(null);
    setFirstName('');
    setLastName('');
    setEmail('');
    setAdmissionYear('');
    setProgramCode('');
    setAdmissionPeriod('');
    setEducationLevel('');
    setSpecialtyCode('');
    setComment('');
    setStep('form');
  };

  const handleCreate = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    if (!firstName.trim() || !lastName.trim() || !email.trim() || !categoryCode) {
      setError(t('auth.error.email'));
      return;
    }
    if (categoryCode === 'DOCTORAL_STUDENT') {
      if (admissionYear === '' || !programCode || !admissionPeriod) {
        setError(t('common.error'));
        return;
      }
    }
    if (categoryCode === 'SUPERVISED_STUDENT') {
      if (!educationLevel || !specialtyCode) {
        setError(t('common.error'));
        return;
      }
    }
    setBusy(true);
    try {
      const created = await accessRequestApi.create(buildPayload());
      syncFromRecord(created);
      const result = await accessRequestApi.startVerification(created.id);
      setCooldown(60);
      setStep('verify');
      notify(t(result.email_sent ? 'auth.access.verifySent' : 'admin.users.toast.emailFailed'), result.email_sent ? 'success' : 'error');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.error'));
    } finally {
      setBusy(false);
    }
  };

  const handleResendVerification = async () => {
    if (!record || cooldown > 0) return;
    setBusy(true);
    setError(null);
    try {
      const result = await accessRequestApi.startVerification(record.id);
      setCooldown(60);
      notify(t(result.email_sent ? 'auth.access.verifySent' : 'admin.users.toast.emailFailed'), result.email_sent ? 'success' : 'error');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.error'));
    } finally {
      setBusy(false);
    }
  };

  const handleRefresh = async () => {
    if (!record) return;
    setBusy(true);
    setError(null);
    try {
      const latest = await accessRequestApi.read(record.id);
      syncFromRecord(latest);
      if (latest.email_status === 'VERIFIED') {
        notify(t('auth.access.emailVerified'), 'success');
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.error'));
    } finally {
      setBusy(false);
    }
  };

  const handleSubmit = async () => {
    if (!record) return;
    setBusy(true);
    setError(null);
    try {
      const submitted = await accessRequestApi.submit(record.id);
      syncFromRecord(submitted);
      localStorage.removeItem(STORAGE_KEY);
      notify(t('auth.registered'), 'success');
      navigate(routes.pending);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.error'));
    } finally {
      setBusy(false);
    }
  };

  const handleStartOver = () => {
    localStorage.removeItem(STORAGE_KEY);
    setRecord(null);
    setCategoryCode('');
    setFirstName('');
    setLastName('');
    setEmail('');
    setAdmissionYear('');
    setProgramCode('');
    setAdmissionPeriod('');
    setEducationLevel('');
    setSpecialtyCode('');
    setStep('category');
    setError(null);
  };

  if (!catalog) {
    return (
      <AuthCard title={t('auth.access.title')} description={t('auth.access.text')} showTabs={false}>
        <p>{error ?? t('common.loading')}</p>
        {error && (
          <button type="button" className="auth-link" onClick={() => navigate(routes.login)}>
            {t('auth.backToLogin')}
          </button>
        )}
      </AuthCard>
    );
  }

  return (
    <AuthCard title={t('auth.access.title')} description={t('auth.access.text')} showTabs={false}>
      {error && <p className="field-error">{error}</p>}

      {(step === 'category' || step === 'form') && (
        <form className="auth-form" onSubmit={handleCreate} noValidate>
          <div className="auth-field">
            <label htmlFor="access-category">{t('auth.access.category')}</label>
            <select
              id="access-category"
              value={categoryCode}
              onChange={(event) => {
                const code = event.target.value;
                if (!code) {
                  setCategoryCode('');
                  setStep('category');
                  return;
                }
                handleSelectCategory(code);
              }}
              required
            >
              <option value="">{t('auth.access.pickCategory')}</option>
              {catalog.categories.map((item) => (
                <option key={item.code} value={item.code}>
                  {item.label[locale]}
                </option>
              ))}
            </select>
          </div>

          {step === 'form' && categoryCode && (
            <>
              <div className="auth-field">
                <label htmlFor="access-first-name">{t('admin.users.field.firstName')}</label>
                <input
                  id="access-first-name"
                  value={firstName}
                  onChange={(event) => setFirstName(event.target.value)}
                  required
                />
              </div>

              <div className="auth-field">
                <label htmlFor="access-last-name">{t('admin.users.field.lastName')}</label>
                <input
                  id="access-last-name"
                  value={lastName}
                  onChange={(event) => setLastName(event.target.value)}
                  required
                />
              </div>

              <div className="auth-field">
                <label htmlFor="access-email">{t('auth.email')}</label>
                <input
                  id="access-email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  placeholder={t('auth.emailPlaceholder')}
                  onChange={(event) => setEmail(event.target.value)}
                  required
                />
              </div>

              {categoryCode === 'DOCTORAL_STUDENT' && (
                <>
                  <div className="auth-field">
                    <label htmlFor="access-year">{t('auth.access.admissionYear')}</label>
                    <select
                      id="access-year"
                      value={admissionYear}
                      onChange={(event) =>
                        setAdmissionYear(event.target.value ? Number(event.target.value) : '')
                      }
                      required
                    >
                      <option value="">—</option>
                      {catalog.doctoral.admission_years.map((year) => (
                        <option key={year} value={year}>
                          {year}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="auth-field">
                    <label htmlFor="access-program">{t('auth.access.program')}</label>
                    <select
                      id="access-program"
                      value={programCode}
                      onChange={(event) => setProgramCode(event.target.value)}
                      required
                    >
                      <option value="">—</option>
                      {catalog.doctoral.programs.map((item) => (
                        <option key={item.value} value={item.value}>
                          {item.label[locale]}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="auth-field">
                    <label htmlFor="access-period">{t('auth.access.period')}</label>
                    <select
                      id="access-period"
                      value={admissionPeriod}
                      onChange={(event) => setAdmissionPeriod(event.target.value)}
                      required
                    >
                      <option value="">—</option>
                      {catalog.doctoral.admission_periods.map((item) => (
                        <option key={item.value} value={item.value}>
                          {item.label[locale]}
                        </option>
                      ))}
                    </select>
                  </div>
                  <p className="auth-note">{catalog.doctoral.department[locale]}</p>
                </>
              )}

              {categoryCode === 'SUPERVISED_STUDENT' && (
                <>
                  <div className="auth-field">
                    <label htmlFor="access-level">{t('auth.access.level')}</label>
                    <select
                      id="access-level"
                      value={educationLevel}
                      onChange={(event) => {
                        setEducationLevel(event.target.value);
                        setSpecialtyCode('');
                      }}
                      required
                    >
                      <option value="">—</option>
                      {catalog.supervised.levels.map((item) => (
                        <option key={item.value} value={item.value}>
                          {item.label[locale]}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="auth-field">
                    <label htmlFor="access-specialty">{t('auth.access.specialty')}</label>
                    <select
                      id="access-specialty"
                      value={specialtyCode}
                      onChange={(event) => setSpecialtyCode(event.target.value)}
                      required
                      disabled={!educationLevel}
                    >
                      <option value="">—</option>
                      {specialties.map((item) => (
                        <option key={item.value} value={item.value}>
                          {item.label[locale]}
                        </option>
                      ))}
                    </select>
                  </div>
                </>
              )}

              <div className="auth-field">
                <label htmlFor="access-comment">
                  {locale === 'kz'
                    ? 'Пікір немесе тілектер (міндетті емес)'
                    : locale === 'en'
                    ? 'Comment or wishes (optional)'
                    : 'Комментарий или пожелания (необязательно)'}
                </label>
                <textarea
                  id="access-comment"
                  rows={3}
                  value={comment}
                  placeholder={
                    locale === 'kz'
                      ? 'Қосымша мәліметтерді немесе қажеттіліктерді көрсетіңіз...'
                      : locale === 'en'
                      ? 'Specify additional details or requirements...'
                      : 'Укажите дополнительные сведения или пожелания...'
                  }
                  onChange={(event) => setComment(event.target.value)}
                />
              </div>

              <button type="submit" className="auth-primary" disabled={busy}>
                {busy ? t('common.loading') : t('auth.submitRegister')}
              </button>
            </>
          )}

          <button type="button" className="auth-link" onClick={() => navigate(routes.login)}>
            {t('auth.backToLogin')}
          </button>
        </form>
      )}

      {step === 'verify' && record && (
        <div className="auth-form">
          <p>
            {t('auth.access.verifyHint', { email: record.email, code: record.reference_code })}
          </p>
          <button type="button" className="auth-primary" disabled={busy} onClick={handleRefresh}>
            {t('auth.access.checkStatus')}
          </button>
          <button
            type="button"
            className="auth-secondary"
            disabled={busy || cooldown > 0}
            onClick={handleResendVerification}
          >
            {cooldown > 0
              ? `${t('auth.access.resend')} (${cooldown}${t('auth.access.seconds')})`
              : t('auth.access.resend')}
          </button>
          <button type="button" className="auth-link" onClick={handleStartOver}>
            {t('auth.access.startOver')}
          </button>
        </div>
      )}

      {step === 'ready' && record && (
        <div className="auth-form">
          <p>{t('auth.access.readyHint', { code: record.reference_code })}</p>
          <button type="button" className="auth-primary" disabled={busy} onClick={handleSubmit}>
            {busy ? t('common.loading') : t('auth.access.submit')}
          </button>
          <button type="button" className="auth-link" onClick={handleStartOver}>
            {t('auth.access.startOver')}
          </button>
        </div>
      )}

      {step === 'submitted' && record && (
        <div className="auth-form">
          <p>{record.status_message || t('auth.pending.text')}</p>
          <p className="auth-note">{record.reference_code}</p>
          <button type="button" className="auth-primary" onClick={() => navigate(routes.pending)}>
            {t('auth.access.goPending')}
          </button>
          <button type="button" className="auth-link" onClick={() => navigate(routes.login)}>
            {t('auth.backToLogin')}
          </button>
        </div>
      )}
    </AuthCard>
  );
};

export default RequestAccessPage;
